// 通知模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { NotificationState, NotificationActions } from "../types";
import { BaseCache } from "../../base/stores";
import { notificationApi } from "../api";
import type { Notification, NoticePageRequest } from "../types";

// 创建通知模块专用的缓存实例
const notificationCache = new BaseCache({
  defaultTTL: 2 * 60 * 1000, // 2分钟
  maxSize: 50,
  enableLogging: true,
});

// 初始状态
const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  cache: new Map(),
  // 消息中心相关状态
  messageNotices: [],
  messageUnreadCount: 0,
  lastNoticeUpdateTime: 0,
  // 用于跟踪userNoticeUpdateTime
  userNoticeUpdateTime: 0,
};

// 创建通知store
export const useNotificationStore = createWithEqualityFn<
  NotificationState & NotificationActions
>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // 通知数据相关
    setNotifications: notifications => {
      const unreadCount = notifications.filter(n => !n.read).length;
      set({ notifications, unreadCount });
    },
    addNotification: notification => {
      const newNotification = {
        ...notification,
        id: Date.now().toString(),
        timestamp: Date.now(),
      };
      const notifications = [newNotification, ...get().notifications];
      const unreadCount = notifications.filter(n => !n.read).length;
      set({ notifications, unreadCount });
    },
    markAsRead: id => {
      const notifications = get().notifications.map(n =>
        n.id === id ? { ...n, read: true } : n
      );
      const unreadCount = notifications.filter(n => !n.read).length;
      set({ notifications, unreadCount });
    },
    markAllAsRead: () => {
      const notifications = get().notifications.map(n => ({
        ...n,
        read: true,
      }));
      set({ notifications, unreadCount: 0 });
    },
    removeNotification: id => {
      const notifications = get().notifications.filter(n => n.id !== id);
      const unreadCount = notifications.filter(n => !n.read).length;
      set({ notifications, unreadCount });
    },
    clearNotifications: () => set({ notifications: [], unreadCount: 0 }),
    setLoading: loading => set({ loading }),
    setError: error => set({ error }),

    // 缓存相关 - 委托给 BaseCache 实例
    setCache: (key, data, ttl) => {
      notificationCache.setCache(key, data, ttl);
      set({ cache: new Map(notificationCache["cache"]) });
    },

    getCache: key => {
      return notificationCache.getCache(key);
    },

    clearCache: pattern => {
      notificationCache.clearCache(pattern);
      set({ cache: new Map(notificationCache["cache"]) });
    },

    hasCache: key => {
      return notificationCache.hasCache(key);
    },

    getCacheKeys: () => {
      return notificationCache.getCacheKeys();
    },

    getCacheSize: () => {
      return notificationCache.getCacheSize();
    },

    cacheFirst: async (key, apiCall, ttl) => {
      return notificationCache.cacheFirst(key, apiCall, ttl);
    },

    // 消息中心相关
    setMessageNotices: notices => {
      const unreadCount = notices.filter(n => !n.isRead).length;
      set({ messageNotices: notices, messageUnreadCount: unreadCount });
    },
    setMessageUnreadCount: count => set({ messageUnreadCount: count }),
    setLastNoticeUpdateTime: time => set({ lastNoticeUpdateTime: time }),

    // 设置用户通知更新时间，只有变化时才重新加载
    setUserNoticeUpdateTime: time => {
      const { userNoticeUpdateTime: currentTime } = get();

      // 只有时间变化了才更新并重新加载
      if (time !== currentTime && time > 0) {
        set({ userNoticeUpdateTime: time });
        // 异步加载未读数
        get().loadMessageUnreadCount();
      }
    },

    loadMessageUnreadCount: async () => {
      try {
        const response = await notificationApi.getNoticePage({
          pageNo: 1,
          pageSize: 20,
        });

        if (response.data?.page?.content) {
          const items = response.data.page.content;
          const unreadCount = items.filter((item: any) => !item.pushed).length;
          set({ messageUnreadCount: unreadCount });

          // 更新最新消息时间
          if (items.length > 0) {
            const latestTime = new Date(items[0].createTime).getTime();
            set({ lastNoticeUpdateTime: latestTime });
          }

          return unreadCount;
        }
        return 0;
      } catch (error) {
        console.error("Failed to load unread count:", error);
        return 0;
      }
    },

    // ========== 业务逻辑方法 ==========

    /**
     * 加载通知列表
     */
    loadNotifications: async (params = {}) => {
      const { setLoading, setError, setNotifications, getCache, setCache } =
        get();

      // 检查缓存
      const cacheKey = `notifications_${JSON.stringify(params)}`;
      const cached = getCache(cacheKey);
      if (cached) {
        setNotifications(cached);
        return cached;
      }

      try {
        setLoading(true);
        setError(null);

        const data = await notificationApi.getNotifications(params);

        // 缓存数据
        setCache(cacheKey, data.list, 1 * 60 * 1000); // 1分钟缓存
        setNotifications(data.list);

        return data.list;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "load notifications failed";
        setError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },

    /**
     * 标记通知为已读（API调用）
     */
    markNotificationAsRead: async (notificationId: string) => {
      const { notifications, setNotifications } = get();

      try {
        await notificationApi.markAsRead(notificationId);

        // 更新本地状态
        const updatedNotifications = notifications.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        );
        setNotifications(updatedNotifications);
      } catch (error) {
        console.error("mark notification as read failed:", error);
        throw error;
      }
    },

    /**
     * 标记所有通知为已读（API调用）
     */
    markAllNotificationsAsRead: async () => {
      const { notifications, setNotifications } = get();

      try {
        await notificationApi.markAllAsRead();

        // 更新本地状态
        const updatedNotifications = notifications.map(notification => ({
          ...notification,
          read: true,
        }));
        setNotifications(updatedNotifications);
      } catch (error) {
        console.error("mark all notifications as read failed:", error);
        throw error;
      }
    },

    /**
     * 删除通知（API调用）
     */
    deleteNotificationById: async (notificationId: string) => {
      const { notifications, setNotifications } = get();

      try {
        await notificationApi.deleteNotification(notificationId);

        // 更新本地状态
        const updatedNotifications = notifications.filter(
          notification => notification.id !== notificationId
        );
        setNotifications(updatedNotifications);
      } catch (error) {
        console.error("delete notification failed:", error);
        throw error;
      }
    },

    /**
     * 获取未读通知数量
     */
    fetchUnreadCount: async () => {
      const { getCache, setCache } = get();

      // 检查缓存
      const cacheKey = "unread_count";
      const cached = getCache(cacheKey);
      if (cached !== null) {
        return cached;
      }

      try {
        const data = await notificationApi.getUnreadCount();

        // 缓存数据
        setCache(cacheKey, data.count, 30 * 1000); // 30秒缓存

        return data.count;
      } catch (error) {
        console.error("获取未读通知数量失败:", error);
        return 0;
      }
    },

    /**
     * 添加本地通知
     */
    addLocalNotification: (
      notification: Omit<Notification, "id" | "timestamp">
    ) => {
      const { notifications, setNotifications } = get();

      const newNotification: Notification = {
        ...notification,
        id: Date.now().toString(),
        timestamp: Date.now(),
      };

      setNotifications([newNotification, ...notifications]);
    },

    /**
     * 获取通知/公告分页列表
     */
    getNoticePage: async (params: NoticePageRequest) => {
      try {
        return await notificationApi.getNoticePage(params);
      } catch (error) {
        console.error("Failed to get notice page:", error);
        throw error;
      }
    },

    /**
     * 清空所有通知
     */
    clearAllNotices: async () => {
      try {
        const result = await notificationApi.clearAllNotices();
        // 刷新消息计数
        await get().loadMessageUnreadCount();
        return result;
      } catch (error) {
        console.error("Failed to clear notices:", error);
        throw error;
      }
    },

    /**
     * 更新通知推送状态（标记为已读）
     */
    updatePushStatus: async (id: string) => {
      try {
        const result = await notificationApi.updatePushStatus(id);
        // 刷新消息计数
        await get().loadMessageUnreadCount();
        return result;
      } catch (error) {
        console.error("Failed to update push status:", error);
        throw error;
      }
    },

    /**
     * 删除单条通知
     */
    deleteNotice: async (id: string) => {
      try {
        const result = await notificationApi.deleteNotice(id);
        // 刷新消息计数
        await get().loadMessageUnreadCount();
        return result;
      } catch (error) {
        console.error("Failed to delete notice:", error);
        throw error;
      }
    },
  }))
);

// 选择器hooks
export const useNotificationData = () =>
  useNotificationStore(state => ({
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    loading: state.loading,
    error: state.error,
  }));

export const useMessageNotificationData = () =>
  useNotificationStore(state => ({
    messageNotices: state.messageNotices,
    messageUnreadCount: state.messageUnreadCount,
    lastNoticeUpdateTime: state.lastNoticeUpdateTime,
    userNoticeUpdateTime: state.userNoticeUpdateTime,
    loadMessageUnreadCount: state.loadMessageUnreadCount,
    setMessageUnreadCount: state.setMessageUnreadCount,
    setLastNoticeUpdateTime: state.setLastNoticeUpdateTime,
    setUserNoticeUpdateTime: state.setUserNoticeUpdateTime,
  }));

export function useNotificationCache() {
  const { cache } = useNotificationStore(state => ({
    cache: state.cache,
  }));
  return cache;
}
